import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useCannotBecause } from '@/lib/permissions';
import { TagsIcon, XIcon } from 'lucide-react';

import { ApiError, api, type BulkOutcome } from '@/lib/api';
import { useT, useTn } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * What you can do to a selection (`plan-beta` B5 + B6).
 *
 * ### It appears, rather than being always present
 *
 * A toolbar of permanently-disabled bulk buttons teaches an operator to ignore
 * that strip of the screen. This exists only when something is selected, which
 * makes its appearance the confirmation that the selection registered.
 *
 * ### Every action is ONE request
 *
 * `api.bulkTransition` posts the id list and gets a per-row report back. The
 * alternative — a loop of single-item calls — is what B5 forbids, and the reason
 * is the report: a bulk certify over a mixed selection SHOULD partly refuse, and
 * an operator needs that as one answer rather than as thirty toasts.
 *
 * ### The refusals are shown, not summarised away
 *
 * A partial success reports as a *warning* naming the first few reasons. "28 of
 * 30 certified" with no explanation of the two is the version of this feature
 * that gets mistrusted after its first mixed selection.
 */

/**
 * The row report both endpoints share.
 *
 * A metadata edit has no coverage *delta* to report — it can move an item into a
 * pool as well as out — so it answers with the whole matrix instead. Only the
 * per-row half is common, and that is what this takes.
 */
interface RowReport {
  requested: number;
  applied: number;
  outcomes: BulkOutcome[];
}

/** Turn a per-row report into one honest sentence, plus the reasons. */
function useReportToast() {
  const t = useT();
  const tn = useTn();

  return (report: RowReport, verb: string) => {
    const refused = report.outcomes.filter((o) => !o.ok);

    if (refused.length === 0) {
      notify.success(tn('bulk.applied', report.applied, { verb }), t('bulk.applied.detail'));
      return;
    }

    // Distinct reasons rather than one per row: thirty items already certified
    // is one fact, not thirty.
    const reasons = [...new Set(refused.map((o) => o.message ?? o.code ?? ''))].slice(0, 3);
    notify.error(
      t('bulk.partial', { applied: report.applied, requested: report.requested, verb }),
      reasons.join(' · '),
    );
  };
}

export function BulkBar({
  selected,
  onClear,
  onDone,
}: {
  selected: Set<string>;
  onClear: () => void;
  /** Refetch the bank and the coverage the transition may have moved. */
  onDone: () => void;
}) {
  const t = useT();
  const tn = useTn();
  const report = useReportToast();
  const [tagging, setTagging] = useState(false);

  const itemIds = [...selected];

  // R3. Every action on this bar is a lifecycle write over an id list —
  // `items:author`, the same capability the per-row buttons carry. Clearing the
  // selection is not: it is local state.
  const cannotAuthor = useCannotBecause('items:author');

  const transition = useMutation({
    mutationFn: (op: 'certify' | 'uncertify' | 'retire') =>
      api.bulkTransition(itemIds, op).then((r) => ({ r, op })),
    onSuccess: ({ r, op }) => {
      report(r, t(`bulk.verb.${op}` as 'bulk.verb.certify'));

      /**
       * The coverage consequence, surfaced separately and as an ERROR.
       *
       * A cell going amber is not a footnote to "24 items retired" — it is the
       * thing that will refuse to generate for a persona tomorrow. The single-item
       * routes have said this since T1; a bulk action that swallowed it would be
       * the quiet version of the same event, at twenty times the scale.
       */
      if (r.impact.newlyWarning.length > 0) {
        notify.error(
          tn('bulk.newlyAmber', r.impact.newlyWarning.length),
          r.impact.newlyWarning.map((c) => `${c.personaId} × ${c.moduleId}`).join(', '),
        );
      }
      onClear();
      onDone();
    },
    onError: (err) =>
      notify.error(
        t('bulk.failed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      ),
  });

  return (
    <>
      {/* A named group: the same verbs appear on every row, so "Certify" alone
          is ambiguous to a screen reader too, not only to a test. */}
      <div
        role="group"
        aria-label={t('bulk.actions')}
        className="bg-accent/60 mb-4 flex flex-wrap items-center gap-2 rounded-md border p-2"
      >
        <span className="ps-1 text-sm font-medium">{tn('bulk.selected', selected.size)}</span>

        {/*
          PROGRESS, such as it honestly is (`plan-beta` E).

          The batch is ONE request, so there is no per-item progress to report —
          the server applies the id list and answers once. An indeterminate bar
          is therefore the truthful shape: it says "this is running and has not
          finished", which is all anyone here knows. A percentage would be
          invented, and a spinner that implied per-item progress would be the
          same invention with better manners.
        */}
        {transition.isPending && (
          <div className="flex min-w-40 items-center gap-2">
            <Progress className="h-1.5 flex-1" />
            <span className="text-muted-foreground text-xs">
              {tn('bulk.applying', selected.size)}
            </span>
          </div>
        )}

        <div className="ms-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={transition.isPending || cannotAuthor !== null}
            title={cannotAuthor ?? undefined}
            onClick={() => transition.mutate('certify')}
          >
            {t('questions.action.certify')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={transition.isPending || cannotAuthor !== null}
            title={cannotAuthor ?? undefined}
            onClick={() => transition.mutate('uncertify')}
          >
            {t('questions.action.uncertify')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={transition.isPending || cannotAuthor !== null}
            title={cannotAuthor ?? undefined}
            onClick={() => transition.mutate('retire')}
          >
            {t('questions.action.retire')}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setTagging(true)}
            disabled={cannotAuthor !== null} title={cannotAuthor ?? undefined}>
            <TagsIcon className="size-4" />
            {t('bulk.tags')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} aria-label={t('bulk.clear')}>
            <XIcon className="size-4" />
          </Button>
        </div>
      </div>

      <Dialog open={tagging} onOpenChange={setTagging}>
        {tagging && (
          <TagDialog
            itemIds={itemIds}
            onClose={() => setTagging(false)}
            onApplied={(r) => {
              report(r, t('bulk.verb.tagged'));
              setTagging(false);
              onClear();
              onDone();
            }}
          />
        )}
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------ tagging (B6) */

const list = (raw: string) => raw.split(',').map((s) => s.trim()).filter(Boolean);

/**
 * Bulk tagging, and the add/remove-versus-replace distinction it is built on.
 *
 * `tags` are **added and removed**, because a selection is rarely homogeneous:
 * tagging 40 items `pilot_2026` means add that, not make it the only tag they
 * have. Audience and context tags **replace**, because they are the fields
 * `pool.query` filters a matrix cell on — "this audience, these contexts" is a
 * statement about the whole set, and merging into it silently widens a pool.
 *
 * Which is why the two are separate fields with different labels rather than one
 * "tags" box with a mode switch nobody reads.
 */
function TagDialog({
  itemIds,
  onClose,
  onApplied,
}: {
  itemIds: string[];
  onClose: () => void;
  onApplied: (report: RowReport) => void;
}) {
  const t = useT();
  const tn = useTn();
  const [add, setAdd] = useState('');
  const [remove, setRemove] = useState('');
  const [audience, setAudience] = useState('');
  const [context, setContext] = useState('');
  const [error, setError] = useState<string | null>(null);

  const apply = useMutation({
    mutationFn: () =>
      api.bulkMeta({
        itemIds,
        ...(list(add).length ? { addTags: list(add) } : {}),
        ...(list(remove).length ? { removeTags: list(remove) } : {}),
        ...(audience.trim() ? { setAudienceTags: list(audience) } : {}),
        ...(context.trim() ? { setContextTags: list(context) } : {}),
      }),
    onSuccess: onApplied,
    onError: (err) => setError(err instanceof ApiError ? err.message : t('bulk.failed')),
  });

  const nothingToDo = !list(add).length && !list(remove).length && !audience.trim() && !context.trim();

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('bulk.tags.title')}</DialogTitle>
        <DialogDescription>{tn('bulk.tags.description', itemIds.length)}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="addTags">{t('bulk.addTags')}</Label>
          <Input id="addTags" value={add} onChange={(e) => setAdd(e.target.value)} placeholder="pilot_2026, reviewed" />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="removeTags">{t('bulk.removeTags')}</Label>
          <Input id="removeTags" value={remove} onChange={(e) => setRemove(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="audienceTags">{t('bulk.audienceTags')}</Label>
          <Input
            id="audienceTags"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="persona.high_schooler"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="contextTags">{t('bulk.contextTags')}</Label>
          <Input id="contextTags" value={context} onChange={(e) => setContext(e.target.value)} />
        </div>

        {/* Stated where the decision is made. Audience and context drive matrix
            pool queries, so replacing them can take items out of a cell. */}
        <Alert>
          <AlertDescription className="text-xs">{t('bulk.replaceWarning')}</AlertDescription>
        </Alert>

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={() => apply.mutate()} disabled={nothingToDo || apply.isPending}>
          {apply.isPending ? t('common.saving') : t('bulk.applyTags')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
